import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { 
  Shield, Users, FileWarning, Heart, LogOut, 
  TrendingUp, DollarSign, CreditCard, UserPlus, MessageCircle,
  ArrowUp, ArrowDown, Calendar, BarChart3, PieChart, Activity,
  Bot, IdCard, Star, RefreshCw
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [adminToken]);

  const fetchAllData = async () => {
    setRefreshing(true);
    try {
      const [overviewRes, revenueRes, userRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API}/admin/analytics/revenue?days=30`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API}/admin/analytics/users?days=30`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        })
      ]);
      setOverview(overviewRes.data);
      setRevenueData(revenueRes.data);
      setUserData(userRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-rose-500 font-serif text-2xl">Loading Analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-800 border-r border-slate-700 p-6 overflow-y-auto hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-serif text-lg font-bold text-white block">SingleSeen</span>
            <span className="text-rose-400 text-xs">Admin CRM</span>
          </div>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <TrendingUp className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-rose-500/10 text-rose-400 font-medium">
            <BarChart3 className="w-5 h-5" />
            Analytics & CRM
          </Link>
          <Link to="/admin/verifications" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <IdCard className="w-5 h-5" />
            ID Verification
            {stats?.id_verifications?.pending > 0 && (
              <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {stats.id_verifications.pending}
              </span>
            )}
          </Link>
          <Link to="/admin/reports" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <FileWarning className="w-5 h-5" />
            Reports
            {stats?.reports?.pending > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {stats.reports.pending}
              </span>
            )}
          </Link>
          <Link to="/admin/users" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <Users className="w-5 h-5" />
            Users
          </Link>
          <Link to="/admin/stories" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <Heart className="w-5 h-5" />
            Love Stories
          </Link>
          <Link to="/admin/testimonials" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <Star className="w-5 h-5" />
            Testimonials
          </Link>
          <Link to="/admin/seed-profiles" className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors">
            <Bot className="w-5 h-5" />
            Seed Profiles
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button onClick={handleLogout} variant="outline" className="w-full border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-white mb-2">Analytics & CRM Dashboard</h1>
            <p className="text-slate-400">Comprehensive business intelligence for SingleSeen</p>
          </div>
          <Button
            onClick={fetchAllData}
            disabled={refreshing}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Revenue Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
              <span className="text-emerald-100 text-sm">Total Revenue</span>
            </div>
            <div className="text-3xl font-bold mb-1">{formatCurrency(overview?.revenue?.total)}</div>
            <p className="text-emerald-100 text-sm">All time</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Calendar className="w-6 h-6" />
              </div>
              <span className="text-blue-100 text-sm">This Month</span>
            </div>
            <div className="text-3xl font-bold mb-1">{formatCurrency(overview?.revenue?.this_month)}</div>
            <p className="text-blue-100 text-sm">{overview?.transactions?.this_month || 0} transactions</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <span className="text-purple-100 text-sm">Total Users</span>
            </div>
            <div className="text-3xl font-bold mb-1">{overview?.users?.total || 0}</div>
            <p className="text-purple-100 text-sm">{overview?.users?.active || 0} active</p>
          </div>

          <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <CreditCard className="w-6 h-6" />
              </div>
              <span className="text-rose-100 text-sm">Subscribers</span>
            </div>
            <div className="text-3xl font-bold mb-1">{overview?.subscriptions?.total_subscribers || 0}</div>
            <p className="text-rose-100 text-sm">{overview?.subscriptions?.conversion_rate || 0}% conversion</p>
          </div>
        </div>

        {/* Second Row - Detailed Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* User Stats */}
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <h3 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              User Statistics
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">New This Week</span>
                <span className="text-white font-medium flex items-center gap-1">
                  <UserPlus className="w-4 h-4 text-emerald-400" />
                  {overview?.users?.new_this_week || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">New This Month</span>
                <span className="text-white font-medium">{overview?.users?.new_this_month || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Male Users</span>
                <span className="text-white font-medium">{overview?.users?.male || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Female Users</span>
                <span className="text-white font-medium">{overview?.users?.female || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Complete Profiles</span>
                <span className="text-white font-medium">{overview?.users?.complete_profiles || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Email Verified</span>
                <span className="text-emerald-400 font-medium">{overview?.users?.email_verified || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">ID Verified</span>
                <span className="text-blue-400 font-medium">{overview?.users?.id_verified || 0}</span>
              </div>
            </div>
          </div>

          {/* Revenue Breakdown */}
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <h3 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-emerald-400" />
              Revenue Breakdown
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Subscriptions</span>
                <span className="text-white font-medium">{formatCurrency(overview?.revenue?.breakdown?.subscriptions)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Connections</span>
                <span className="text-white font-medium">{formatCurrency(overview?.revenue?.breakdown?.connections)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Contact Exchanges</span>
                <span className="text-white font-medium">{formatCurrency(overview?.revenue?.breakdown?.contact_exchanges)}</span>
              </div>
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Total Transactions</span>
                  <span className="text-white font-medium">{overview?.transactions?.total || 0}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-400">Avg Transaction</span>
                  <span className="text-white font-medium">{formatCurrency(revenueData?.average_transaction)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Stats */}
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <h3 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-rose-400" />
              Subscription Tiers
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-amber-400 font-medium">Basic Plan</span>
                  <span className="text-white font-bold">{overview?.subscriptions?.basic || 0}</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full"
                    style={{ width: `${overview?.subscriptions?.total_subscribers > 0 ? (overview?.subscriptions?.basic / overview?.subscriptions?.total_subscribers * 100) : 0}%` }}
                  />
                </div>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-rose-400 font-medium">Premium Plan</span>
                  <span className="text-white font-bold">{overview?.subscriptions?.premium || 0}</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-rose-500 rounded-full"
                    style={{ width: `${overview?.subscriptions?.total_subscribers > 0 ? (overview?.subscriptions?.premium / overview?.subscriptions?.total_subscribers * 100) : 0}%` }}
                  />
                </div>
              </div>
              <div className="text-center pt-4">
                <div className="text-4xl font-bold text-white">{overview?.subscriptions?.conversion_rate || 0}%</div>
                <div className="text-slate-400 text-sm">Conversion Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <h3 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              Engagement Metrics
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                <div className="text-3xl font-bold text-white">{overview?.engagement?.total_chats || 0}</div>
                <div className="text-slate-400 text-sm">Total Chats</div>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                <div className="text-3xl font-bold text-white">{overview?.engagement?.total_messages || 0}</div>
                <div className="text-slate-400 text-sm">Messages Sent</div>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                <div className="text-3xl font-bold text-white">{overview?.engagement?.total_interests_sent || 0}</div>
                <div className="text-slate-400 text-sm">Interests Sent</div>
              </div>
              <div className="p-4 bg-slate-900/50 rounded-xl text-center">
                <div className="text-3xl font-bold text-white">{overview?.engagement?.total_photo_requests || 0}</div>
                <div className="text-slate-400 text-sm">Photo Requests</div>
              </div>
            </div>
          </div>

          {/* Top Countries */}
          <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
            <h3 className="font-serif text-xl text-white mb-6">Top Countries</h3>
            <div className="space-y-3">
              {userData?.top_countries?.slice(0, 5).map((country, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-sm w-6">{idx + 1}.</span>
                    <span className="text-white">{country.country || 'Unknown'}</span>
                  </div>
                  <span className="text-slate-400">{country.count} users</span>
                </div>
              ))}
              {(!userData?.top_countries || userData.top_countries.length === 0) && (
                <p className="text-slate-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h3 className="font-serif text-xl text-white mb-6">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link 
              to="/admin/users"
              className="p-4 bg-slate-900/50 rounded-xl text-center hover:bg-slate-700 transition-colors"
            >
              <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <div className="text-white font-medium">Manage Users</div>
            </Link>
            <Link 
              to="/admin/verifications"
              className="p-4 bg-slate-900/50 rounded-xl text-center hover:bg-slate-700 transition-colors"
            >
              <IdCard className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <div className="text-white font-medium">Verifications</div>
              {stats?.id_verifications?.pending > 0 && (
                <div className="text-amber-400 text-sm">{stats.id_verifications.pending} pending</div>
              )}
            </Link>
            <Link 
              to="/admin/reports"
              className="p-4 bg-slate-900/50 rounded-xl text-center hover:bg-slate-700 transition-colors"
            >
              <FileWarning className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <div className="text-white font-medium">Reports</div>
              {stats?.reports?.pending > 0 && (
                <div className="text-red-400 text-sm">{stats.reports.pending} pending</div>
              )}
            </Link>
            <Link 
              to="/admin/testimonials"
              className="p-4 bg-slate-900/50 rounded-xl text-center hover:bg-slate-700 transition-colors"
            >
              <Star className="w-8 h-8 text-rose-400 mx-auto mb-2" />
              <div className="text-white font-medium">Testimonials</div>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminAnalyticsPage;
